/**
 * Transactional WhatsApp templates. Params are an exact allow-list per template: free-form client
 * data (workNotes, sensitiveNotes, tags) has no key here, so it cannot be passed (compile-time) and
 * is rejected by assertSafeParams (runtime). No promotional template exists in this build.
 */
import type { ConsentPurpose } from '../../domain/types';

/** Allowed param keys per template. */
export const TEMPLATE_KEYS = {
  booking_confirmed: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'manageUrl'],
  reminder_24h: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'manageUrl'],
  rescheduled: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'oldDate', 'oldTime', 'manageUrl'],
  cancelled_by_owner: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'bookUrl'],
  cancelled_by_client_ack: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'bookUrl'],
  waitlist_offer: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'offerUrl', 'expiresTime'],
  waitlist_offer_taken: ['businessName', 'clientName', 'serviceName', 'date', 'time'],
  deposit_request: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'amount', 'payUrl', 'holdMinutes'],
  deposit_received: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'amount', 'manageUrl'],
  deposit_expired: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'bookUrl'],
  /** To the OWNER when a client taps "I paid" for a Bit/PayBox deposit. `method` is a display name (Bit / PayBox). */
  deposit_claimed_owner: ['businessName', 'clientName', 'serviceName', 'date', 'time', 'amount', 'method'],
  /** To a client whose future appointment was cancelled because the business closed its Planella account. No booking link: the page is gone. */
  business_closed: ['businessName', 'clientName', 'serviceName', 'date', 'time'],
} as const;

export type TemplateId = keyof typeof TEMPLATE_KEYS;
export const TEMPLATE_IDS = Object.keys(TEMPLATE_KEYS) as TemplateId[];

/** Params for a template: every key is a pre-formatted display string. */
export type TemplateParams<K extends TemplateId> = { readonly [P in (typeof TEMPLATE_KEYS)[K][number]]: string };

/** Discriminated union: `{template, params}` pairs that type-check together. */
export type TemplateCall = { [K in TemplateId]: { template: K; params: TemplateParams<K> } }[TemplateId];

export interface TemplateMeta {
  purpose: ConsentPurpose;
  /** Sent while the client is actively on the checkout screen: exempt from quiet hours. */
  interactive: boolean;
}
export const TEMPLATE_META: Record<TemplateId, TemplateMeta> = {
  booking_confirmed: { purpose: 'reminders', interactive: false },
  reminder_24h: { purpose: 'reminders', interactive: false },
  rescheduled: { purpose: 'reminders', interactive: false },
  cancelled_by_owner: { purpose: 'reminders', interactive: false },
  cancelled_by_client_ack: { purpose: 'reminders', interactive: false },
  waitlist_offer: { purpose: 'reminders', interactive: false },
  waitlist_offer_taken: { purpose: 'reminders', interactive: false },
  deposit_request: { purpose: 'reminders', interactive: true },
  deposit_received: { purpose: 'reminders', interactive: true },
  deposit_expired: { purpose: 'reminders', interactive: false },
  deposit_claimed_owner: { purpose: 'reminders', interactive: true },
  business_closed: { purpose: 'reminders', interactive: true },
};

export function isTemplateId(x: string): x is TemplateId {
  return Object.prototype.hasOwnProperty.call(TEMPLATE_KEYS, x);
}

/** Runtime guard behind the types: unknown keys (e.g. sensitiveNotes) and non-strings are rejected. */
export function assertSafeParams(template: string, params: Record<string, unknown>): void {
  if (!isTemplateId(template)) throw new Error(`unknown template: ${template}`);
  const allowed: readonly string[] = TEMPLATE_KEYS[template];
  for (const [k, v] of Object.entries(params)) {
    if (!allowed.includes(k)) throw new Error(`param "${k}" is not allowed in template ${template}`);
    if (typeof v !== 'string') throw new Error(`param "${k}" must be a string`);
  }
  for (const k of allowed) if (!(k in params)) throw new Error(`missing param "${k}" for template ${template}`);
}

/**
 * Hebrew fallback bodies used when the UX locale files do not (yet) define `messages.<template>`.
 * UX owns the real copy in locales/<lang>/messages.json; this keeps the demo outbox free of raw keys.
 */
export const FALLBACK_TEXT_HE: Record<TemplateId, string> = {
  booking_confirmed: 'היי {clientName}, התור ל{serviceName} ב{businessName} נקבע ל{date} בשעה {time}. לניהול התור: {manageUrl}',
  reminder_24h: 'היי {clientName}, מזכירים שיש לך תור ל{serviceName} ב{businessName}, {date} בשעה {time}. לשינוי או ביטול: {manageUrl}',
  rescheduled: 'היי {clientName}, התור ב{businessName} הוזז מ{oldDate} {oldTime} אל {date} בשעה {time}. לניהול התור: {manageUrl}',
  cancelled_by_owner: 'היי {clientName}, התור ל{serviceName} ב{date} בשעה {time} בוטל על ידי {businessName}. לקביעת תור חדש: {bookUrl}',
  cancelled_by_client_ack: 'היי {clientName}, התור ל{serviceName} ב{date} בשעה {time} בוטל. לקביעת תור חדש: {bookUrl}',
  waitlist_offer: 'היי {clientName}, התפנה תור ל{serviceName} ב{businessName}, {date} בשעה {time}. מי שמאשר ראשון מקבל אותו, עד {expiresTime}: {offerUrl}',
  waitlist_offer_taken: 'היי {clientName}, התור שהוצע ל{date} בשעה {time} נתפס. נשארת ברשימת ההמתנה ונעדכן כשיתפנה עוד.',
  deposit_request: 'היי {clientName}, כדי לשמור את התור ל{serviceName} ב{date} בשעה {time} נדרשת מקדמה של {amount}. התור שמור ל-{holdMinutes} דקות: {payUrl}',
  deposit_received: 'היי {clientName}, התקבלה מקדמה של {amount}. התור ל{serviceName} ב{date} בשעה {time} שמור. לניהול התור: {manageUrl}',
  deposit_claimed_owner: '{clientName} סימנה ששילמה מקדמה של {amount} ב-{method} עבור {serviceName}, {date} בשעה {time}. נא לאשר ב-{businessName} שההעברה התקבלה.',
  business_closed: 'היי {clientName}, התור ל{serviceName} ב{date} בשעה {time} בוטל כי {businessName} סגרה את הדף ב-Planella. כדאי ליצור קשר ישירות עם העסק.',
  deposit_expired: 'היי {clientName}, לא התקבלה מקדמה ולכן התור ל{serviceName} ב{date} בשעה {time} שוחרר. לקביעת תור חדש: {bookUrl}',
};
